import type { Editor as TinyMCEEditorInstance } from 'tinymce';
import { AIResponse, EditorRefObject } from '../types';
import { getElement } from './generalizationHelpers';
import {
  DuplicateTableResult,
  assignNewTargetNodeIds,
  duplicateTableInEditor,
  getTableForTargetNodeId,
  getTargetEditorWindowScroll,
  restoreTargetViewAfterDuplicateTableUndo,
  DuplicateTableUndoContext,
  resolveUndoneDuplicateTableContext,
  wasDuplicateTableUndone,
} from './tableDuplicateHelpers';

export type TableTransferResult = DuplicateTableResult & {
  sourceToTargetNodeIdMap: Record<string, string>;
};

export type TableTransferSuggestionPayload = Record<string, unknown> & {
  sessionId?: number;
  targetNodeId: string;
  sourceNodeId: string;
  documentId: string | number;
  sourceFileId: string | number;
  targetValue: string;
  sourceValue: string;
  mapperType: 'author';
  targetFileId?: string | number;
};

export const getTableForSourceNodeId = (
  editorRef: EditorRefObject,
  nodeId: string,
): HTMLTableElement | null => {
  const element = getElement(editorRef, nodeId);
  return (element?.closest('table') as HTMLTableElement | null) ?? null;
};

export const getTargetTableInsertionHost = (
  editorRef: EditorRefObject,
  anchorNodeId: string,
): HTMLElement | null => {
  const element = getElement(editorRef, anchorNodeId);
  if (!element) {
    return null;
  }

  return (
    (element.closest('table') as HTMLTableElement | null) ??
    (element.closest('p, div, section, li') as HTMLElement | null) ??
    element
  );
};

export const insertTransferredTableAtTargetAnchor = (
  sourceTable: HTMLTableElement,
  targetDocId: string,
  targetEditorRef: EditorRefObject,
  targetAnchorNodeId: string,
): TableTransferResult | null => {
  const insertionHost = getTargetTableInsertionHost(targetEditorRef, targetAnchorNodeId);
  if (!insertionHost?.parentNode) {
    return null;
  }

  const clone = sourceTable.cloneNode(true) as HTMLTableElement;
  const sourceToTargetNodeIdMap = assignNewTargetNodeIds(clone, targetDocId);
  insertionHost.parentNode.insertBefore(clone, insertionHost.nextSibling);

  return {
    oldToNewNodeIdMap: sourceToTargetNodeIdMap,
    sourceToTargetNodeIdMap,
    newRootTable: clone,
  };
};

export const transferSourceTableInTargetEditor = (
  targetEditor: TinyMCEEditorInstance,
  sourceTable: HTMLTableElement,
  targetDocId: string,
  targetAnchorNodeId: string,
  targetEditorRef: EditorRefObject,
): TableTransferResult | null => {
  let result: TableTransferResult | null = null;
  targetEditor.undoManager.transact(() => {
    result = insertTransferredTableAtTargetAnchor(
      sourceTable,
      targetDocId,
      targetEditorRef,
      targetAnchorNodeId,
    );
  });
  if (result) {
    targetEditor.nodeChanged();
  }
  return result;
};

const getSourceCellContent = (sourceTable: HTMLTableElement, sourceNodeId: string): string => {
  const sourceCell = sourceTable.querySelector(
    `[data-nodeid="${CSS.escape(sourceNodeId)}"]`,
  ) as HTMLElement | null;
  return sourceCell?.innerHTML ?? '';
};

export const buildAiResponseForTransferredTable = (
  sourceToTargetNodeIdMap: Record<string, string>,
  sourceTable: HTMLTableElement,
  sourceFileId: number,
): AIResponse => {
  const response: AIResponse = {};

  Object.entries(sourceToTargetNodeIdMap).forEach(([sourceNodeId, targetNodeId]) => {
    const value = getSourceCellContent(sourceTable, sourceNodeId);
    response[targetNodeId] = [{ [sourceNodeId]: { value, sourceFileId } }];
  });

  return response;
};

export const buildAuthorTransferSuggestions = (
  sourceToTargetNodeIdMap: Record<string, string>,
  sourceTable: HTMLTableElement,
  sourceFileId: number,
  targetFileId: number,
  sessionId?: number,
): TableTransferSuggestionPayload[] =>
  Object.entries(sourceToTargetNodeIdMap).map(([sourceNodeId, targetNodeId]) => {
    const sourceValue = getSourceCellContent(sourceTable, sourceNodeId);
    return {
      sessionId,
      targetNodeId,
      sourceNodeId,
      documentId: sourceFileId,
      sourceFileId,
      targetFileId,
      targetValue: sourceValue,
      sourceValue,
      mapperType: 'author',
    };
  });

export {
  getTableForTargetNodeId,
  getTargetEditorWindowScroll,
  restoreTargetViewAfterDuplicateTableUndo,
  resolveUndoneDuplicateTableContext,
  wasDuplicateTableUndone,
  duplicateTableInEditor,
};
export type { DuplicateTableUndoContext };
